import { h, Component, Fragment } from 'preact';

// Freshly-uploaded images resize on-demand on the image server, so requests can
// fail until the sized thumbnail exists. Retry with a growing delay (capped) so
// slow conversions still show up without hammering the server.
const MAX_ERROR_RETRIES = 20;
const BASE_RETRY_DELAY_MS = 1500;
const MAX_RETRY_DELAY_MS = 10000;

// Delay before retry attempt (errorRetries = number of retries already done).
function retryDelayMs(errorRetries) {
    return Math.min(BASE_RETRY_DELAY_MS * (1.5 ** errorRetries), MAX_RETRY_DELAY_MS);
}

// Cache-bust only after an error so a not-yet-generated thumbnail reloads;
// unchanged for images that load on the first try (errorRetries === 0).
function cacheBustedSrc(src, errorRetries) {
    if (!src || errorRetries < 1) {
        return src;
    }
    return `${src}${src.includes('?') ? '&' : '?'}retry=${errorRetries}`;
}

class LazyImage extends Component {
    constructor(props) {
        super(props);
        const { lowResBase64, src } = props;
        this.state = {
            lowResImage: lowResBase64 ? `data:image/jpeg;base64,${lowResBase64}` : '',
            highResImage: src,
            isLoaded: false,  // Track if the image is fully loaded
            errorRetries: 0,  // How many times we've retried a not-yet-ready src
        };
        // Retry generation errors with cache-busting and backoff. The finite
        // budget covers a few minutes without polling expired/broken URLs forever.
        this.maxErrorRetries = MAX_ERROR_RETRIES;
        this.retryTimer = null;
        this.retryExhaustedNotified = false;
    }

    componentWillUnmount() {
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    handleImageLoad = () => {
        // Once the high-res image has loaded, update the state to indicate it's fully loaded
        this.setState({ isLoaded: true });
    }

    handleImageError = () => {
        // Thumbnail may still be generating on the image server — retry with a
        // cache-busted src so it appears once ready.
        const configuredMax = Number(this.props.maxErrorRetries);
        const maxErrorRetries = Number.isInteger(configuredMax) && configuredMax > 0
            ? configuredMax
            : this.maxErrorRetries;
        if (this.state.isLoaded || this.state.errorRetries >= maxErrorRetries) {
            if (!this.state.isLoaded && !this.retryExhaustedNotified
                && typeof this.props.onRetryExhausted === 'function') {
                this.retryExhaustedNotified = true;
                this.props.onRetryExhausted();
            }
            return;
        }
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
            this.setState(s => ({ errorRetries: s.errorRetries + 1 }));
        }, retryDelayMs(this.state.errorRetries));
    }

    render() {
        const { lowResImage, highResImage, isLoaded, errorRetries } = this.state;
        const highResSrc = cacheBustedSrc(highResImage, errorRetries);
        const {
            alt,
            className,
            style,
            onClick,
            onDoubleClick = () => {},
            // src/lowResBase64 are rendered via state (highResSrc/lowResImage);
            // they must NOT end up in restProps — the spread below would clobber
            // the cache-busted retry src and dump the base64 onto the DOM.
            src,
            lowResBase64,
            maxErrorRetries,
            onRetryExhausted,
            ...restProps
        } = this.props;

        return (
            <div class={`lazy-image-container ${className || ''}`}>

                {/* High-res image that will fade in */}
                {highResImage ? <>
                    <img
                        src={highResSrc}
                        alt={alt || 'Image'}
                        onLoad={this.handleImageLoad}
                        onError={this.handleImageError}
                        onDoubleClick={onDoubleClick}
                        onClick={onClick}
                        style={`
                            width: 100%;
                            height: auto;
                            opacity: ${isLoaded ? 1 : 0};
                            transition: opacity 0.1s ease-in;
                            object-fit: cover;
                            ${style ? style : ''};
                        `}
                        {...restProps} // Spread the remaining props (e.g., onClick, data-attributes, etc.)
                    />
                </> : <>
                    {/* Low-res placeholder image */}
                    <img
                        src={lowResImage}
                        alt={alt || 'Image loading...'}
                        onDoubleClick={onDoubleClick}
                        onClick={onClick}
                        style={`
                            width: 100%;
                            height: auto;
                            opacity: ${isLoaded ? 0 : 1};
                            transition: opacity 0.1s ease-out;
                            object-fit: cover;
                            ${style ? style : ''};
                        `}
                        {...restProps} // Spread the remaining props (e.g., onClick, data-attributes, etc.)
                    />
                </>}
            </div>
        );
    }
}

export { retryDelayMs, cacheBustedSrc, MAX_ERROR_RETRIES, BASE_RETRY_DELAY_MS, MAX_RETRY_DELAY_MS };
export default LazyImage;
